import "client-only";

import axiosInstance from "./axiosInstance";
import { store } from "@/src/store";
import {
  setCredentials,
  clearCredentials,
} from "@/src/store/slices/authSlice";

let isRefreshing = false;
let isInitialized = false;
// قائمة انتظار للـ requests اللي فشلت بسبب 401 ريحانها لحد ما الـ token يتجدد
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
};

export const setupInterceptors = () => {
  if (isInitialized) return;
  isInitialized = true;

  // ─── Request: ضيف الـ access token في كل request ─────────
  axiosInstance.interceptors.request.use(
    (config) => {
      const accessToken = store.getState().auth.accessToken;
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // ─── Response: handle 401 → silent refresh ────────────────
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // لو مش 401 أو الـ request ده هو الـ refresh request نفسه → ابعت الـ error
      if (
        error.response?.status !== 401 ||
        originalRequest._retry ||
        originalRequest.url?.includes("refresh-token")
      ) {
        return Promise.reject(error);
      }

      // لو في refresh جاري → ضيف الـ request في قائمة الانتظار
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await fetch("/api/auth", {
          method: "GET",
          cache: "no-store",
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw error;
        }

        const data = await response.json();

        const newAccessToken = data.access;

        // حفظ الـ access token الجديد في Redux
        store.dispatch(
          setCredentials({
            accessToken: newAccessToken,
            userId: store.getState().auth.userId,
          })
        );

        // حدّث الـ Authorization header للـ request الأصلي
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // الـ refresh فشل → logout
        processQueue(refreshError, null);
        store.dispatch(clearCredentials());
        void fetch("/api/auth", {
          method: "DELETE",
          cache: "no-store",
          credentials: "same-origin",
        });

        // Redirect للـ login (client-side فقط)
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};
